import { useState, useRef, useEffect, useCallback } from 'react'
import { api } from '../lib/api'
import { getGoogleMapsUrl, type StructuredAddress } from '../lib/mapbox'

interface Props {
  value: Partial<StructuredAddress>
  onChange: (address: StructuredAddress) => void
  placeholder?: string
  required?: boolean
  showMapPreview?: boolean
}

interface Suggestion {
  formatted: string
  address_line1: string
  city: string
  state: string
  zip_code: string
  country: string
  latitude: number | null
  longitude: number | null
  mapbox_place_id: string | null
  static_map_url: string | null
}

export function AddressAutocomplete({ value, onChange, placeholder = 'Start typing an address...', required, showMapPreview = true }: Props) {
  const [query, setQuery] = useState(value.address_line1 || '')
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [loading, setLoading] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Sync external value changes
  useEffect(() => {
    if (value.address_line1 && value.address_line1 !== query) {
      setQuery(value.address_line1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.address_line1])

  const fetchSuggestions = useCallback(async (q: string) => {
    if (q.length < 3) { setSuggestions([]); return }
    setLoading(true)
    try {
      const results = await api.get<Suggestion[]>(`/geocode?query=${encodeURIComponent(q)}`)
      setSuggestions(results)
      setShowDropdown(results.length > 0)
    } catch {
      setSuggestions([])
    } finally {
      setLoading(false)
    }
  }, [])

  const handleInputChange = (val: string) => {
    setQuery(val)
    // Also update parent with typed value so manual entry works
    onChange({ ...value as StructuredAddress, address_line1: val })

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300)
  }

  const handleSelect = (s: Suggestion) => {
    setQuery(s.address_line1)
    setShowDropdown(false)
    setSuggestions([])
    onChange({
      address_line1: s.address_line1,
      address_line2: value.address_line2 || '',
      city: s.city,
      state: s.state,
      zip_code: s.zip_code,
      country: s.country,
      latitude: s.latitude,
      longitude: s.longitude,
      mapbox_place_id: s.mapbox_place_id,
      static_map_url: s.static_map_url,
    })
  }

  // Close dropdown on outside click
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [])

  const hasCoords = value.latitude != null && value.longitude != null

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        type="text"
        value={query}
        onChange={e => handleInputChange(e.target.value)}
        onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
        placeholder={placeholder}
        required={required}
        className="input w-full"
        style={{ padding: '8px 12px', fontSize: 14, border: '1px solid var(--border-light)', borderRadius: 8 }}
        autoComplete="off"
      />
      {loading && (
        <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: 'var(--text-tertiary)' }}>
          ...
        </div>
      )}

      {showDropdown && suggestions.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
          background: 'var(--bg-surface)', border: '1px solid var(--border-light)',
          borderRadius: 8, marginTop: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          maxHeight: 220, overflowY: 'auto',
        }}>
          {suggestions.map((s, i) => (
            <div
              key={i}
              onClick={() => handleSelect(s)}
              style={{
                padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                borderBottom: i < suggestions.length - 1 ? '1px solid var(--border-subtle)' : undefined,
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <div style={{ fontWeight: 500 }}>{s.address_line1}</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                {[s.city, s.state, s.zip_code].filter(Boolean).join(', ')}
              </div>
            </div>
          ))}
        </div>
      )}

      {showMapPreview && hasCoords && (
        <div style={{ marginTop: 8 }}>
          <a
            href={getGoogleMapsUrl(value.latitude!, value.longitude!)}
            target="_blank"
            rel="noopener noreferrer"
            title="Open in Google Maps"
          >
            {value.static_map_url ? (
              <img
                src={value.static_map_url}
                alt="Map preview"
                style={{ width: '100%', maxWidth: 300, height: 150, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border-light)' }}
              />
            ) : (
              <div style={{
                width: '100%', maxWidth: 300, height: 80,
                borderRadius: 8, border: '1px solid var(--border-light)',
                background: 'var(--bg-hover)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 6, fontSize: 13, color: 'var(--text-secondary)',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                View on Google Maps
              </div>
            )}
          </a>
        </div>
      )}
    </div>
  )
}
